import { ChevronLeft, ChevronRight } from '@untitled-ui/icons-react/build/cjs';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ActionTooltip from 'src/components/ActionTooltip';
import MetaCard from 'src/components/MetaCard';
import StatusDotProfileCmp from 'src/components/StatusDotProfileCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { getFormattedNumber, getPredictiveIndexTitleAndColor } from 'src/utils/CommonFunctions';

import { useRadarData } from '../hooks/useRadarData';
import { selectSelectedLeg, selectSelectedLegIndex, setSelectedLeg, trackDetailLoading as trackDetailLoadingSelector, trackOrderDetail as trackOrderDetailSelector } from '../track.slice';
import { isCheckCommonCarrier } from '../track.utils';

import { SHARE_MOBILE_CARD } from './sections/common/shareMobile.styles';

// Leg pager (multi-leg orders). Exported so the share-link mobile sheet can show it on the Schedule
// tab — switching legs changes the schedule cards there.
export const LegSwitcher = ({ isShareMobile = false }: { isShareMobile?: boolean }) => {
    const trackOrderDetail = useAppSelector(trackOrderDetailSelector);
    const isDetailLoading = useAppSelector(trackDetailLoadingSelector);
    // Store selectors, not useRadarData: that hook also runs the route-distance fetch effect,
    // and this pager renders alongside sections that already run it.
    const dispatch = useAppDispatch();
    const selectedLeg = useAppSelector(selectSelectedLeg);
    const selectedLegIndex = useAppSelector(selectSelectedLegIndex);
    const handleSelectLeg = (leg: any, index: number) => dispatch(setSelectedLeg({ leg, index }));

    if ((trackOrderDetail?.legs?.length ?? 0) <= 1) return null;

    return (
        <div className={`flex items-center justify-between ${isShareMobile ? `${SHARE_MOBILE_CARD} px-3 py-2.5` : 'bg-white rounded-lg shadow-md px-4 py-3'}`}>
            <span className={`text-xs lg:text-sm font-semibold text-grayLight900 ${isDetailLoading ? 'custom-loading' : ''}`}>{isDetailLoading ? 'WAL-XXXX-XXXX' : selectedLeg?.legId ?? '-'}</span>
            <div className="flex items-center gap-x-1">
                <button
                    type="button"
                    disabled={isDetailLoading || selectedLegIndex === 0}
                    onClick={() => {
                        const prev = selectedLegIndex - 1;
                        handleSelectLeg(trackOrderDetail.legs[prev], prev);
                    }}
                    className="p-1 rounded hover:bg-gray50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                    <ChevronLeft className="w-4 h-4 text-grayLight600" />
                </button>
                <span className="text-xs text-grayLight600 font-medium min-w-[32px] text-center">
                    {selectedLegIndex + 1} / {trackOrderDetail.legs.length}
                </span>
                <button
                    type="button"
                    disabled={isDetailLoading || selectedLegIndex === trackOrderDetail.legs.length - 1}
                    onClick={() => {
                        const next = selectedLegIndex + 1;
                        handleSelectLeg(trackOrderDetail.legs[next], next);
                    }}
                    className="p-1 rounded hover:bg-gray50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                    <ChevronRight className="w-4 h-4 text-grayLight600" />
                </button>
            </div>
        </div>
    );
};

interface IProps {
    // Share-link mobile sheet (Overview tab): route timeline + stat cards per Figma "Track Link - V3".
    isShareMobile?: boolean;
    // Line under the consignee in the route timeline (delivered date / ETA) — trusted helper HTML.
    consigneeSubText?: string;
}

const OrderMetaCards = ({ isShareMobile = false, consigneeSubText }: IProps) => {
    const { t } = useTranslation();
    const trackOrderDetail = useAppSelector(trackOrderDetailSelector);
    const isDetailLoading = useAppSelector(trackDetailLoadingSelector);
    const { selectedLeg, fullRouteDistance, remainingDistance } = useRadarData();
    const shipperLocationRef = useRef<HTMLParagraphElement>(null);
    const consigneeLocationRef = useRef<HTMLParagraphElement>(null);

    const shipper = useMemo(() => {
        const legs = trackOrderDetail?.legs ?? [];

        return legs[0]?.shipper ?? null;
    }, [trackOrderDetail]);

    const consignee = useMemo(() => {
        const legs = trackOrderDetail?.legs ?? [];

        return legs[legs.length - 1]?.consignee ?? null;
    }, [trackOrderDetail]);

    // While loading, selectedLeg still holds the PREVIOUS order's leg — computing the
    // predictive index from it flashes stale data ("On Track") during the skeleton state.
    const predictive = isDetailLoading ? null : getPredictiveIndexTitleAndColor(selectedLeg?.lastLocationUpdate?.predictiveLocationIndex);

    const dotColor =
        predictive?.badgeType === 'success' ? 'green' : predictive?.badgeType === 'red' ? 'red' : predictive?.badgeType === 'yellow' || predictive?.badgeType === 'warning' ? 'yellow' : 'green';

    // Visibility comes from LOADED data only (same as WAL radar) — forcing the card
    // visible while loading made it vanish after load on LTL orders (layout jump).
    const isPredictiveVisible = !!trackOrderDetail?.orderType && trackOrderDetail.orderType.toLowerCase() !== 'ltl' && !isCheckCommonCarrier(selectedLeg?.carrier?.type);

    // Remaining Distance is shown for LTL orders only (same as WAL radar), sitting
    // beside Total Distance to fill the row.
    const isLtl = !!trackOrderDetail?.orderType && trackOrderDetail.orderType.toLowerCase() === 'ltl';

    const renderPredictive = (statusLabelClassName: string) => (
        <ActionTooltip
            childrenClassName="leading-[18px] !flex h-[18px]"
            message={
                <div className="flex flex-col max-w-[265px] min-w-[190px] lg:min-w-[265px]">
                    <p className="text-xs text-textSecondary font-normal justify-start text-left flex mb-2">
                        Predictive Location Index (PLI) is a tracking confidence metric used in logistics and shipment visibility systems.
                    </p>
                    <p className="text-xs text-textSecondary font-normal text-left flex">
                        In simple terms, it tells you how accurate and reliable the current (and predicted) location of a shipment is.
                    </p>
                </div>
            }
        >
            <ActionTooltip
                childrenClassName="leading-[18px] !flex"
                message={
                    isDetailLoading ? (
                        ''
                    ) : (
                        <div className="flex flex-col py-1.5 px-2 max-w-[265px] min-w-[190px] lg:min-w-[265px]">
                            <p className="text-xs font-medium text-grayLight900 flex mb-1">{t(predictive?.titleKey as any)}</p>
                            <p className="text-xs text-textSecondary font-normal justify-start text-left flex">{t(predictive?.reasonKey as any)}</p>
                        </div>
                    )
                }
                type="light"
                openOnHover
                tooltipPosition="center"
                hideArrowWhenNoMessage
            >
                <div className="border-b border-dashed border-borderPrimary cursor-pointer w-fit">
                    <StatusDotProfileCmp dotColor={dotColor} label={t(predictive?.titleKey as any)} statusLabelClassName={statusLabelClassName} />
                </div>
            </ActionTooltip>
        </ActionTooltip>
    );

    const legSwitcher = <LegSwitcher />;

    const shipperLabel = isDetailLoading ? 'lorem ipsum dolor' : shipper ? `${shipper.city}, ${shipper.stateCode} ${shipper.postal}` : '-';
    const consigneeLabel = isDetailLoading ? 'lorem ipsum dolor' : consignee ? `${consignee.city}, ${consignee.stateCode} ${consignee.postal}` : '-';

    const shipperTooltip = isDetailLoading ? (
        ''
    ) : (
        <div className="flex flex-col max-w-[265px] min-w-[190px] lg:min-w-[265px]">
            <p className="text-xs font-medium text-grayLight900 flex mb-1">{shipper?.companyName ?? '-'}</p>
            <p className="text-xs text-textSecondary font-normal justify-start text-left flex">{shipper?.fullAddress ?? '-'}</p>
        </div>
    );

    const consigneeTooltip = isDetailLoading ? (
        ''
    ) : (
        <div className="flex flex-col max-w-[265px] min-w-[190px] lg:min-w-[265px]">
            <p className="text-xs font-medium text-grayLight900 flex mb-1">{consignee?.companyName ?? '-'}</p>
            <p className="text-xs text-textSecondary font-normal justify-start text-left flex">{consignee?.fullAddress ?? '-'}</p>
        </div>
    );

    if (isShareMobile) {
        const renderStat = (label: string, value: React.ReactNode, loadingText = 'lorem ipsum dolor') => (
            <div className={`${SHARE_MOBILE_CARD} p-2.5 flex flex-col gap-0.5 min-w-0`}>
                <span className="text-[10px] leading-[14px] font-medium text-gray500">{label}</span>
                <span className={`text-sm leading-5 font-semibold text-grayLight900 truncate ${isDetailLoading ? 'custom-loading w-fit' : ''}`}>{isDetailLoading ? loadingText : value}</span>
            </div>
        );

        return (
            <>
                {/* Route timeline — Shipper → Consignee */}
                <div className={`${SHARE_MOBILE_CARD} p-3.5 flex flex-col`}>
                    <div className="flex gap-3">
                        <div className="flex flex-col items-center gap-1 pt-[3px] w-3 flex-none self-stretch">
                            <span className="w-2.5 h-2.5 rounded-full border-2 border-primary bg-white flex-none" />
                            <span className="w-0.5 flex-1 bg-utilityGray200" />
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col gap-0.5 pb-4">
                            <p className="text-[10px] leading-[14px] font-medium tracking-[0.4px] uppercase text-gray500">Shipper</p>
                            <ActionTooltip childrenClassName="!flex min-w-0" message={shipperTooltip} type="light" openOnHover tooltipPosition="center" hideArrowWhenNoMessage>
                                <TruncateTextCmp truncateTextParentClassName="min-w-0" refName={shipperLocationRef} message={isDetailLoading ? '' : shipperLabel} noTooltip>
                                    <p ref={shipperLocationRef} className={`text-[13px] leading-[18px] font-medium text-grayLight900 truncate ${isDetailLoading ? 'custom-loading w-fit' : ''}`}>
                                        {shipperLabel}
                                    </p>
                                </TruncateTextCmp>
                            </ActionTooltip>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <div className="flex flex-col items-center pt-[3px] w-3 flex-none">
                            <span className="w-2.5 h-2.5 rounded-full bg-successSecondary flex-none" />
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                            <p className="text-[10px] leading-[14px] font-medium tracking-[0.4px] uppercase text-gray500">Consignee</p>
                            <ActionTooltip childrenClassName="!flex min-w-0" message={consigneeTooltip} type="light" openOnHover tooltipPosition="center" hideArrowWhenNoMessage>
                                <TruncateTextCmp truncateTextParentClassName="min-w-0" refName={consigneeLocationRef} message={isDetailLoading ? '' : consigneeLabel} noTooltip>
                                    <p ref={consigneeLocationRef} className={`text-[13px] leading-[18px] font-medium text-grayLight900 truncate ${isDetailLoading ? 'custom-loading w-fit' : ''}`}>
                                        {consigneeLabel}
                                    </p>
                                </TruncateTextCmp>
                            </ActionTooltip>
                            {/* Trusted helper HTML (<sup> ordinals) — same source as the sheet's ETA line. */}
                            {!isDetailLoading && consigneeSubText && (
                                <p className="text-[11px] leading-4 font-normal text-grayLight600 truncate" dangerouslySetInnerHTML={{ __html: consigneeSubText }} />
                            )}
                        </div>
                    </div>
                </div>

                {/* Stat cards — same visibility rules as the default grid below. Figma: 8px between cards in a
                    row, 12px between rows (each row is its own block in the tab pane's 12px stack). */}
                <div className="grid grid-cols-2 gap-x-2 gap-y-3">
                    {isPredictiveVisible && (
                        <div className={`${SHARE_MOBILE_CARD} p-2.5 flex flex-col gap-0.5 min-w-0`}>
                            <span className="text-[10px] leading-[14px] font-medium text-gray500">Predictive Location Index</span>
                            {renderPredictive('!font-semibold !text-sm !leading-5')}
                        </div>
                    )}
                    {renderStat('Service Type', trackOrderDetail?.orderType?.toUpperCase() ?? '-', 'lorem')}
                    {renderStat('Equipment Type', trackOrderDetail?.legs?.[0]?.equipmentTypeFullName ?? '-')}
                    <div className={isDetailLoading || isPredictiveVisible || isLtl ? 'min-w-0' : 'col-span-2'}>
                        {renderStat('Total Distance', fullRouteDistance ? `${getFormattedNumber(Math.round(fullRouteDistance), false, false, true)} miles` : '-')}
                    </div>
                    {(isDetailLoading || isLtl) && renderStat('Remaining Distance', remainingDistance ? `${getFormattedNumber(Math.round(remainingDistance), false, false, true)} miles` : '-')}
                </div>
            </>
        );
    }

    return (
        <>
            <div className="grid grid-cols-2 gap-2">
                {/* Shipper */}
                <MetaCard label="Shipper">
                    <ActionTooltip
                        childrenClassName="leading-[18px] !flex"
                        message={
                            isDetailLoading ? (
                                ''
                            ) : (
                                <div className="flex flex-col max-w-[265px] min-w-[190px] lg:min-w-[265px]">
                                    <p className="text-xs font-medium text-grayLight900 flex mb-1">{shipper?.companyName ?? '-'}</p>
                                    <p className="text-xs text-textSecondary font-normal justify-start text-left flex">{shipper?.fullAddress ?? '-'}</p>
                                </div>
                            )
                        }
                        type="light"
                        openOnHover
                        tooltipPosition="center"
                        hideArrowWhenNoMessage
                    >
                        <TruncateTextCmp
                            truncateTextParentClassName={isDetailLoading ? 'flex-1' : ''}
                            refName={shipperLocationRef}
                            message={isDetailLoading ? '' : shipper ? `${shipper.city}, ${shipper.stateCode} ${shipper.postal}` : '-'}
                            noTooltip
                        >
                            <p
                                ref={shipperLocationRef}
                                className={`text-xs lg:text-sm lg:leading-[22px] font-medium text-grayLight900 ${
                                    isDetailLoading ? 'custom-loading w-full' : 'border-b border-dashed border-borderPrimary cursor-pointer'
                                } truncate`}
                            >
                                {isDetailLoading ? 'lorem ipsum dolor' : shipper ? `${shipper.city}, ${shipper.stateCode} ${shipper.postal}` : '-'}
                            </p>
                        </TruncateTextCmp>
                    </ActionTooltip>
                </MetaCard>

                {/* Consignee */}
                <MetaCard label="Consignee">
                    <ActionTooltip
                        childrenClassName="leading-[18px] !flex"
                        message={
                            isDetailLoading ? (
                                ''
                            ) : (
                                <div className="flex flex-col max-w-[265px] min-w-[190px] lg:min-w-[265px]">
                                    <p className="text-xs font-medium text-grayLight900 flex mb-1">{consignee?.companyName ?? '-'}</p>
                                    <p className="text-xs text-textSecondary font-normal justify-start text-left flex">{consignee?.fullAddress ?? '-'}</p>
                                </div>
                            )
                        }
                        type="light"
                        openOnHover
                        tooltipPosition="center"
                        hideArrowWhenNoMessage
                    >
                        <TruncateTextCmp
                            truncateTextParentClassName={isDetailLoading ? 'flex-1' : ''}
                            refName={consigneeLocationRef}
                            message={isDetailLoading ? '' : consignee ? `${consignee.city}, ${consignee.stateCode} ${consignee.postal}` : '-'}
                            noTooltip
                        >
                            <p
                                ref={consigneeLocationRef}
                                className={`text-xs lg:text-sm lg:leading-[22px] font-medium text-grayLight900 ${
                                    isDetailLoading ? 'custom-loading w-full' : 'border-b border-dashed border-borderPrimary cursor-pointer'
                                } truncate`}
                            >
                                {isDetailLoading ? 'lorem ipsum dolor' : consignee ? `${consignee.city}, ${consignee.stateCode} ${consignee.postal}` : '-'}
                            </p>
                        </TruncateTextCmp>
                    </ActionTooltip>
                </MetaCard>

                {/* Predictive Location Index */}
                {isPredictiveVisible && <MetaCard label="Predictive Location Index">{renderPredictive('!font-medium !text-[12px] lg:!text-[14px] leading-[18px] lg:leading-[22px]')}</MetaCard>}

                {/* Service Type */}
                <MetaCard label="Service Type">
                    <StatusDotProfileCmp
                        dotColor="green"
                        label={isDetailLoading ? 'lorem' : trackOrderDetail?.orderType?.toUpperCase() ?? '-'}
                        statusLabelClassName="!font-medium !text-[12px] lg:!text-[14px] leading-[18px] lg:leading-[22px]"
                        parentClassName={isDetailLoading ? 'custom-loading' : ''}
                    />
                </MetaCard>

                {/* Equipment Type */}
                <MetaCard label="Equipment Type">
                    <span className={`text-xs lg:text-sm lg:leading-[22px] font-medium text-grayLight900 ${isDetailLoading ? 'custom-loading' : ''}`}>
                        {isDetailLoading ? 'lorem ipsum dolor' : trackOrderDetail?.legs?.[0]?.equipmentTypeFullName ?? '-'}
                    </span>
                </MetaCard>

                {/* Total Distance — full width only when Predictive Location Index is hidden AND
                    the LTL Remaining Distance card is not present (odd card count, nothing beside it).
                    While loading, the Remaining Distance skeleton sits beside it, so never full width. */}
                <div className={isDetailLoading || isPredictiveVisible || isLtl ? '' : 'col-span-2'}>
                    <MetaCard label="Total Distance">
                        <span className={`text-xs lg:text-sm lg:leading-[22px] font-medium text-grayLight900 ${isDetailLoading ? 'custom-loading' : ''}`}>
                            {/* Math.round before formatting: WAL's getFormattedNumber rounds when isDecimal=false, the portal's
                                truncates — without it Remaining Distance reads a mile lower here than on WAL (209 vs 210). */}
                            {isDetailLoading ? 'lorem ipsum dolor' : fullRouteDistance ? `${getFormattedNumber(Math.round(fullRouteDistance), false, false, true)} miles` : '-'}
                        </span>
                    </MetaCard>
                </div>

                {/* Remaining Distance — LTL only, mirrors WAL radar. Also rendered as a skeleton
                    while loading so the grid stays a balanced 2×3 with no card popping in/out
                    when an LTL order finishes loading. */}
                {(isDetailLoading || isLtl) && (
                    <MetaCard label="Remaining Distance">
                        <span className={`text-xs lg:text-sm lg:leading-[22px] font-medium text-grayLight900 ${isDetailLoading ? 'custom-loading' : ''}`}>
                            {isDetailLoading ? 'lorem ipsum dolor' : remainingDistance ? `${getFormattedNumber(Math.round(remainingDistance), false, false, true)} miles` : '-'}
                        </span>
                    </MetaCard>
                )}
            </div>

            {legSwitcher}
        </>
    );
};

export default OrderMetaCards;
